import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IConfigMarker,
  ICliente,
  IVistaMarker,
  TipoVehiculo,
  FuncionActivo,
  IActivo,
  IGrupo,
  IQueryParam,
  IFilter,
} from 'modelos/src';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ConfigMarkersService } from '../../../../../auxiliares/servicios/http/config-markers.service';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-crear-editar-marker',
  templateUrl: './crear-editar-marker.component.html',
  styleUrls: ['./crear-editar-marker.component.scss'],
  standalone: false,
})
export class CrearEditarMarkerComponent implements OnInit {
  data = inject<IConfigMarker>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<CrearEditarMarkerComponent>>(MatDialogRef);
  helper = inject(HelperService);
  private service = inject(ConfigMarkersService);
  private lista2Service = inject(Lista2Service);

  public loading = false;
  public form?: FormGroup;
  public title?: string;

  public clientePropio?: ICliente = LoginService.getCliente();
  public scopes = [
    {
      label: 'Cliente',
      value: 'cliente',
    },
    {
      label: 'Flota',
      value: 'grupo',
    },
    {
      label: 'Vehículo',
      value: 'activo',
    },
  ];

  public tiposVehiculo: TipoVehiculo[] = [
    'Auto',
    'Camion',
    'Camioneta',
    'Colectivo',
    'Grua',
    'Moto',
    'Otro',
  ];
  public tiposLabel: Record<TipoVehiculo, string> = {
    Auto: 'Auto',
    Camion: 'Camión',
    Camioneta: 'Camioneta',
    Colectivo: 'Colectivo',
    Grua: 'Grúa',
    Moto: 'Moto',
    Otro: 'Otro',
  };

  public funcionesVehiculo: FuncionActivo[] = [
    'Ambulancia',
    'Bomberos',
    'Mantenimiento',
    'Otro',
    'Particular',
    'Policia',
    'Seguridad Privada',
    'Servicio Técnico',
    'Transporte',
  ];
  public funcionesLabel: Record<FuncionActivo, string> = {
    Particular: 'Particular',
    'Seguridad Privada': 'Seguridad Privada',
    Mantenimiento: 'Mantenimiento',
    Transporte: 'Transporte',
    Bomberos: 'Bomberos',
    Policia: 'Policía',
    Ambulancia: 'Ambulancia',
    'Servicio Técnico': 'Servicio Técnico',
    Otro: 'Otro',
  };

  // Opciones de los selectores de tipo y función
  public readonly opcionesTipoVehiculo = this.tiposVehiculo.map((valor) => ({
    valor,
    etiqueta: this.tiposLabel[valor],
  }));
  public readonly opcionesFuncionVehiculo = this.funcionesVehiculo.map(
    (valor) => ({ valor, etiqueta: this.funcionesLabel[valor] }),
  );

  public getTipoLabel(value: string): string {
    return this.tiposLabel[value as TipoVehiculo] ?? value;
  }

  public getFuncionLabel(value: string): string {
    return this.funcionesLabel[value as FuncionActivo] ?? value;
  }

  public grupos: IGrupo[] = [];
  public activos: IActivo[] = [];
  public expandedCards: boolean[] = [];
  public expandedTipoIndex: number | null = null;
  public expandedFuncionIndex: number | null = null;

  get scopeSeleccionado(): 'cliente' | 'grupo' | 'activo' | null {
    return this.form?.get('scope')?.value || null;
  }

  get vistas(): FormArray {
    return this.form?.get('vistas') as FormArray;
  }

  public isCardExpanded(index: number): boolean {
    return this.expandedCards[index] !== false; // true por defecto
  }

  public onPanelTipoOpened(vistaControl: any): void {
    const index = this.getVistaIndex(vistaControl);
    this.expandedTipoIndex = index;
    // Cerrar todos los otros paneles de tipo
    this.getVistasTipo().forEach((control: any) => {
      const idx = this.getVistaIndex(control);
      if (idx !== index) {
        this.expandedCards[idx] = false;
      } else {
        this.expandedCards[idx] = true;
      }
    });
  }

  public onPanelTipoClosed(vistaControl: any): void {
    const index = this.getVistaIndex(vistaControl);
    this.expandedCards[index] = false;
    if (this.expandedTipoIndex === index) {
      this.expandedTipoIndex = null;
    }
  }

  public onPanelFuncionOpened(vistaControl: any): void {
    const index = this.getVistaIndex(vistaControl);
    this.expandedFuncionIndex = index;
    // Cerrar todos los otros paneles de función
    this.getVistasFuncion().forEach((control: any) => {
      const idx = this.getVistaIndex(control);
      if (idx !== index) {
        this.expandedCards[idx] = false;
      } else {
        this.expandedCards[idx] = true;
      }
    });
  }

  public onPanelFuncionClosed(vistaControl: any): void {
    const index = this.getVistaIndex(vistaControl);
    this.expandedCards[index] = false;
    if (this.expandedFuncionIndex === index) {
      this.expandedFuncionIndex = null;
    }
  }

  private crearVistaFormGroup(vista?: IVistaMarker): FormGroup {
    // Determinar categoría inicial basado en qué valor existe (por defecto: tipo)
    const categoriaInicial = vista?.tipoVehiculo
      ? 'tipo'
      : vista?.funcionVehiculo
        ? 'funcion'
        : 'tipo';

    const formGroup = new FormGroup({
      categoria: new FormControl(categoriaInicial),
      tipoVehiculo: new FormControl(
        vista?.tipoVehiculo ||
          (categoriaInicial === 'tipo' ? this.tiposVehiculo[0] : null),
      ),
      funcionVehiculo: new FormControl(
        vista?.funcionVehiculo ||
          (categoriaInicial === 'funcion' ? this.funcionesVehiculo[0] : null),
      ),
      vehicleTypeIcon: new FormControl(vista?.vehicleTypeIcon),
      vehiculeTypeIconColor: new FormControl(vista?.vehiculeTypeIconColor),
      vehicleFunctionIcon: new FormControl(vista?.vehicleFunctionIcon),
      vehicleFunctionIconColor: new FormControl(
        vista?.vehicleFunctionIconColor,
      ),
      useOriginalIconColor: new FormControl(
        vista?.useOriginalIconColor || false,
      ),
      groupColorIcon: new FormControl(vista?.groupColorIcon || false),
      groupColorBackground: new FormControl(
        vista?.groupColorBackground || false,
      ),
      offColor: new FormControl(vista?.offColor),
      stopColor: new FormControl(vista?.stopColor),
      movingColor: new FormControl(vista?.movingColor),
      movingDegraded: new FormControl(vista?.movingDegraded || false),
    });

    // Suscribirse a cambios de categoría para limpiar el campo opuesto
    formGroup.get('categoria')?.valueChanges.subscribe((categoria) => {
      if (categoria === 'tipo') {
        formGroup.get('funcionVehiculo')?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehicleFunctionIcon')
          ?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehicleFunctionIconColor')
          ?.setValue(null, { emitEvent: false });
      } else if (categoria === 'funcion') {
        formGroup.get('tipoVehiculo')?.setValue(null, { emitEvent: false });
        formGroup.get('vehicleTypeIcon')?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehiculeTypeIconColor')
          ?.setValue(null, { emitEvent: false });
      } else {
        // Si se selecciona "Ninguna", limpiar ambos
        formGroup.get('tipoVehiculo')?.setValue(null, { emitEvent: false });
        formGroup.get('funcionVehiculo')?.setValue(null, { emitEvent: false });
        formGroup.get('vehicleTypeIcon')?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehiculeTypeIconColor')
          ?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehicleFunctionIcon')
          ?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehicleFunctionIconColor')
          ?.setValue(null, { emitEvent: false });
      }
    });

    // Lógica de exclusión de colores
    // Si se activa "useOriginalIconColor", limpiar colores de icono y desactivar groupColorIcon
    formGroup
      .get('useOriginalIconColor')
      ?.valueChanges.subscribe((useOriginal) => {
        if (useOriginal) {
          formGroup
            .get('vehiculeTypeIconColor')
            ?.setValue(null, { emitEvent: false });
          formGroup
            .get('vehicleFunctionIconColor')
            ?.setValue(null, { emitEvent: false });
          formGroup
            .get('groupColorIcon')
            ?.setValue(false, { emitEvent: false });
        }
      });

    // Si se selecciona un color de icono (tipo), desactivar useOriginalIconColor y groupColorIcon
    formGroup.get('vehiculeTypeIconColor')?.valueChanges.subscribe((color) => {
      if (color) {
        formGroup
          .get('useOriginalIconColor')
          ?.setValue(false, { emitEvent: false });
        formGroup.get('groupColorIcon')?.setValue(false, { emitEvent: false });
      }
    });

    // Si se selecciona un color de icono (función), desactivar useOriginalIconColor y groupColorIcon
    formGroup
      .get('vehicleFunctionIconColor')
      ?.valueChanges.subscribe((color) => {
        if (color) {
          formGroup
            .get('useOriginalIconColor')
            ?.setValue(false, { emitEvent: false });
          formGroup
            .get('groupColorIcon')
            ?.setValue(false, { emitEvent: false });
        }
      });

    // Si se activa groupColorIcon, limpiar colores de icono y desactivar useOriginalIconColor
    formGroup.get('groupColorIcon')?.valueChanges.subscribe((groupIcon) => {
      if (groupIcon) {
        formGroup
          .get('vehiculeTypeIconColor')
          ?.setValue(null, { emitEvent: false });
        formGroup
          .get('vehicleFunctionIconColor')
          ?.setValue(null, { emitEvent: false });
        formGroup
          .get('useOriginalIconColor')
          ?.setValue(false, { emitEvent: false });
      }
    });

    return formGroup;
  }

  // Función auxiliar para aplicar valores por defecto a una vista
  private aplicarValoresPorDefecto(
    vista: FormGroup,
    categoria: 'tipo' | 'funcion',
    tipoOFuncion: string,
  ): void {
    const nombreArchivo = tipoOFuncion.toLowerCase().replace(/\s+/g, ' ');
    const esTipo = categoria === 'tipo';

    vista.patchValue({
      categoria: categoria,
      tipoVehiculo: esTipo ? tipoOFuncion : null,
      funcionVehiculo: esTipo ? null : tipoOFuncion,
      vehicleTypeIcon: esTipo
        ? `assets/map/vehiculos/Markers Default/Tipo/${nombreArchivo}.png`
        : null,
      vehiculeTypeIconColor: esTipo ? '#616161' : null,
      vehicleFunctionIcon: esTipo
        ? null
        : `assets/map/vehiculos/Markers Default/Funcion/${nombreArchivo}.png`,
      vehicleFunctionIconColor: esTipo ? null : '#616161',
      useOriginalIconColor: false,
      groupColorIcon: false,
      groupColorBackground: false,
      offColor: '#616161',
      stopColor: '#1976d2',
      movingColor: '#388e3c',
      movingDegraded: false,
    });
  }

  public agregarVista(): void {
    // Obtener tipos y funciones ya existentes
    const tiposExistentes = this.vistas.controls
      .filter((c: any) => c.get('categoria')?.value === 'tipo')
      .map((c: any) => c.get('tipoVehiculo')?.value);

    const funcionesExistentes = this.vistas.controls
      .filter((c: any) => c.get('categoria')?.value === 'funcion')
      .map((c: any) => c.get('funcionVehiculo')?.value);

    // Crear configuraciones solo para tipos que no existan
    this.tiposVehiculo.forEach((tipo) => {
      if (!tiposExistentes.includes(tipo)) {
        const vista = this.crearVistaFormGroup();
        this.aplicarValoresPorDefecto(vista, 'tipo', tipo);
        this.vistas.push(vista);
        this.expandedCards.push(false);
      }
    });

    // Crear configuraciones solo para funciones que no existan
    this.funcionesVehiculo.forEach((funcion) => {
      if (!funcionesExistentes.includes(funcion)) {
        const vista = this.crearVistaFormGroup();
        this.aplicarValoresPorDefecto(vista, 'funcion', funcion);
        this.vistas.push(vista);
        this.expandedCards.push(false);
      }
    });
  }

  public eliminarVista(index: number): void {
    this.vistas.removeAt(index);
    this.expandedCards.splice(index, 1);
  }

  public getCategoriaVista(vistaControl: any): 'tipo' | 'funcion' | null {
    return vistaControl.get('categoria')?.value || null;
  }

  public getVistasTipo(): any[] {
    return this.vistas.controls.filter(
      (control: any) => control.get('categoria')?.value === 'tipo',
    );
  }

  public getVistasFuncion(): any[] {
    return this.vistas.controls.filter(
      (control: any) => control.get('categoria')?.value === 'funcion',
    );
  }

  public getVistaIndex(vistaControl: any): number {
    return this.vistas.controls.indexOf(vistaControl);
  }

  private createForm() {
    let vistasArray: FormArray;

    // Si es crear (sin data o sin vistas), crear configuraciones según scope
    if (!this.data?.vistas || this.data.vistas.length === 0) {
      vistasArray = new FormArray([]);

      // Para scope 'activo', solo crear 2 vistas: una de tipo y una de función
      if (this.data?.scope === 'activo') {
        // Vista de tipo
        const vistaTipo = this.crearVistaFormGroup();
        this.aplicarValoresPorDefecto(vistaTipo, 'tipo', this.tiposVehiculo[0]);
        vistasArray.push(vistaTipo);

        // Vista de función
        const vistaFuncion = this.crearVistaFormGroup();
        this.aplicarValoresPorDefecto(
          vistaFuncion,
          'funcion',
          this.funcionesVehiculo[0],
        );
        vistasArray.push(vistaFuncion);
      } else {
        // Para scope 'cliente' y 'grupo', crear todas las configuraciones
        // Crear configuraciones para todos los tipos de vehículo
        this.tiposVehiculo.forEach((tipo) => {
          const vista = this.crearVistaFormGroup();
          this.aplicarValoresPorDefecto(vista, 'tipo', tipo);
          vistasArray.push(vista);
        });

        // Crear configuraciones para todas las funciones
        this.funcionesVehiculo.forEach((funcion) => {
          const vista = this.crearVistaFormGroup();
          this.aplicarValoresPorDefecto(vista, 'funcion', funcion);
          vistasArray.push(vista);
        });
      }
    } else {
      // Si es editar, usar las vistas existentes
      vistasArray = new FormArray(
        this.data.vistas.map((v) => this.crearVistaFormGroup(v)),
      );
    }

    // Inicializar array de expansión (todos contraídos)
    this.expandedCards = new Array(vistasArray.length).fill(false);

    this.form = new FormGroup({
      global: new FormControl(this.data?.global || false),
      idUsuario: new FormControl(this.data?.idUsuario),
      scope: new FormControl(
        this.data?.scope || 'cliente',
        Validators.required,
      ),
      idRef: new FormControl(this.data?.idRef, Validators.required),
      vistas: vistasArray,
    });

    // Cuando cambia el scope, resetear el idRef y cargar datos correspondientes
    this.form.get('scope')?.valueChanges.subscribe((scope) => {
      this.form?.get('idRef')?.reset();
      this.cargarDatosSegunScope(scope);

      // Si cambia a 'activo', resetear vistas a solo 2 (tipo y función)
      if (scope === 'activo') {
        this.resetVistasParaActivo();
      }
    });
  }

  private resetVistasParaActivo(): void {
    const vistasControl = this.form?.get('vistas') as FormArray;
    if (!vistasControl) return;

    // Limpiar todas las vistas existentes
    while (vistasControl.length > 0) {
      vistasControl.removeAt(0);
    }

    // Crear solo 2 vistas: una de tipo y una de función
    const vistaTipo = this.crearVistaFormGroup();
    this.aplicarValoresPorDefecto(vistaTipo, 'tipo', this.tiposVehiculo[0]);
    vistasControl.push(vistaTipo);

    const vistaFuncion = this.crearVistaFormGroup();
    this.aplicarValoresPorDefecto(
      vistaFuncion,
      'funcion',
      this.funcionesVehiculo[0],
    );
    vistasControl.push(vistaFuncion);

    // Actualizar array de expansión
    this.expandedCards = [false, false];
  }

  private async cargarDatosSegunScope(scope: 'cliente' | 'grupo' | 'activo') {
    try {
      if (scope === 'grupo') {
        const filter: IFilter<IGrupo> = {
          categoria: 'Flota',
        };
        const query: IQueryParam = {
          filter: JSON.stringify(filter),
          sort: 'nombre',
          select: 'idCliente nombre color',
          includeChildren: true,
        };
        const data = await this.lista2Service.grupos(query).toPromise();
        this.grupos = data?.datos || [];
      } else if (scope === 'activo') {
        const data = await this.lista2Service
          .vehiculos({ limit: 1000, includeChildren: true })
          .toPromise();
        this.activos = data?.datos || [];
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      this.helper.notifError(error);
    }
  }

  public async onSubmit() {
    this.loading = true;
    let res = null;
    try {
      const data = this.getData();
      if (this.data?._id) {
        // Update
        await this.service.update(this.data._id, data);
        this.helper.notifSuccess('Configuración de Marker actualizada');
      } else {
        // Create
        res = await this.service.create(data);
        this.helper.notifSuccess('Configuración de Marker creada');
      }
      this.dialogRef.close(res?._id);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public async subirIcono(
    categoria: 'tipo' | 'funcion',
    vistaIndex: number,
  ): Promise<void> {
    try {
      // Crear un input file temporal
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      // Esperar a que el usuario seleccione un archivo
      input.onchange = async (event: any) => {
        const file = event.target?.files?.[0];
        if (!file) return;

        // Validar que sea una imagen
        if (!file.type.startsWith('image/')) {
          this.helper.notifError('Seleccioná una imagen válida');
          return;
        }

        this.loading = true;
        try {
          // Obtener valores del formulario
          const idRef = this.form?.get('idRef')?.value || 'sin-ref';
          const idUsuario = this.form?.get('idUsuario')?.value || 'sin-usuario';

          // Obtener el valor específico de tipo o función desde la vista
          const vista = this.vistas.at(vistaIndex) as FormGroup;
          let tipoOFuncion = '';

          if (categoria === 'tipo') {
            tipoOFuncion = vista.get('tipoVehiculo')?.value || 'sin-tipo';
          } else {
            tipoOFuncion = vista.get('funcionVehiculo')?.value || 'sin-funcion';
          }

          // Limpiar el nombre (eliminar espacios y caracteres especiales)
          tipoOFuncion = tipoOFuncion.toLowerCase().replace(/\s+/g, '-');

          // Generar nombre con el formato: marker_tipoOFuncion_idRef_idUsuario
          const extension = file.name.split('.').pop();
          const nombreImagen = `marker_${tipoOFuncion}_${idRef}_${idUsuario}.${extension}`;

          // Subir imagen usando el helper service
          const resultado = await this.helper.subirImagen(
            file,
            nombreImagen,
            'irix-assets',
          );

          // Agregar timestamp a la URL para evitar cache
          const urlConCache = `${resultado.url}?date=${Date.now()}`;

          // Asignar la URL al campo correspondiente
          if (categoria === 'tipo') {
            vista.get('vehicleTypeIcon')?.setValue(urlConCache);
          } else {
            vista.get('vehicleFunctionIcon')?.setValue(urlConCache);
          }

          this.helper.notifSuccess('Imagen subida correctamente');
        } catch (error) {
          console.error('Error al subir imagen:', error);
          this.helper.notifError('Error al subir la imagen');
        } finally {
          this.loading = false;
        }
      };

      // Trigger el click del input
      input.click();
    } catch (error) {
      console.error('Error al abrir selector de archivo:', error);
      this.helper.notifError('Error al abrir selector de archivo');
    }
  }

  public getData(): Partial<IConfigMarker> {
    const formValue = this.form?.value;
    const data: Partial<IConfigMarker> = {
      global: formValue.global,
      idUsuario: formValue.idUsuario,
      scope: formValue.scope,
      idRef: formValue.idRef,
      vistas: formValue.vistas,
    };
    return data;
  }

  async ngOnInit() {
    this.title = this.data?._id
      ? `Editar configuración de marker`
      : 'Crear configuración de marker';
    this.createForm();

    // Cargar datos iniciales si ya tiene un scope definido
    if (this.data?.scope) {
      await this.cargarDatosSegunScope(this.data.scope);
    }
  }
}
